import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { useGetLoggedInUser } from '@revelio/auth';
import {
  BuildADashTrackingEvents,
  CenterLoading,
  FeatureFlag,
} from '@revelio/core';
import { useFirstLoadGateBlocker } from '@revelio/core/first-load-gate';

import { usePageVisitTracking } from '../../hooks/usePageVisitTracking';
import { useBrand } from '../../shared/branding/brand-provider';
import { DataInspectorFilterSync } from '../../shared/data-inspector/data-inspector-filter-sync';
import { useUnleashUserFlagsReady } from '../../shared/unleash-flags/unleash-flags';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { CustomDashPreferenceProivder } from '../../shared/user-preferences/CustomDashPreferenceProivder';
import { SyncInitialPreferencesToFilters } from '../../shared/user-preferences/SyncInitialPreferencesToFilters';
import { GlobalAIAssistantBridge } from '../global-ai-assistant/global-ai-assistant-bridge';
import { takeGeneratedPage } from '../global-ai-assistant/pending-generated-page';
import { useGlobalAIAssistantFeatureEnabled } from '../global-ai-assistant/use-global-ai-assistant-feature-enabled';
import { CustomDash } from './CustomDash';
import {
  PRIMARY_FILTER_BY_AI_ENTITY,
  seedTabContentWithLlmContent,
} from './components/AIAssistant/llm-tab-content-dispatch';
import { DashboardErrorBoundary } from './components/DashboardErrorBoundary';
import { EditModeProvider } from './components/EditModeProvider/EditModeProvider';
import {
  DEFAULT_FULL_PAGE_ERROR_THRESHOLD,
  FullPageError,
} from './components/FullPageError/FullPageError';
import { PageProvider } from './components/PageConfigProvider/PageConfigProvider';
import { type Page, PageType } from './components/PageConfigProvider/types';
import type { PositionedItem } from './components/PageConfigProvider/types/item';
import { usePageManager } from './components/PageManagerProvider/PageManagerProvider';
import { PageNotFound } from './components/PageNotFound';
import { PageStateProvider } from './components/PageState/PageStateProvider';
import { scrollPositionStore } from './components/scroll-position-store';
import { FilterProvider } from './filters/contexts/FilterProvider';
import { FilterId } from './filters/types/filters';
import { useFetchPageConfig } from './hooks/useFetchPageConfig';
import { ReportDocumentProvider } from './report-document/ReportDocumentProvider';
import {
  pageFromReportDocument,
  reportDocumentFromPage,
  withReportDocumentPages,
} from './report-document/report-document-converters';
import type { ReportDocumentConfig } from './report-document/report-document-types';
import {
  useFetchReportDocument,
  useReportDocumentsQuery,
} from './report-document/use-report-documents';
import { getLocalReportTemplate } from './templates/reports/report-templates-registry';
import {
  getFilterPersistenceProps,
  getFilterScopeKey,
} from './utils/filter-persistence';
import { generatePageDraftConfig } from './utils/generate-page-draft';
import {
  getLocalTemplate,
  getLocalTemplates,
} from './utils/page-config-lookup';
import {
  isAnalyticsEntity,
  isAnalyticsRevelioPage,
} from './utils/revelio-primary-filter-entity-switch';
import {
  buildPathFromPage,
  type EntityType,
  extractEntityTypeFromPath,
  isValidEntityType,
  type RouteAction,
} from './utils/routing-helpers';
import { setLastViewedTab } from './utils/tab-state';

const CustomDashContextWrapper = ({
  editMode,
  pageConfig,
  isReport,
  isUnsavedDraft,
  reportDocument,
}: {
  editMode: boolean;
  pageConfig: Page;
  isReport?: boolean;
  /** `pageConfig` is a freshly-generated, never-persisted draft (e.g. AI build). */
  isUnsavedDraft?: boolean;
  /** Set when the report mounts on ReportDocumentProvider instead of PageProvider. */
  reportDocument?: {
    initialConfig: ReportDocumentConfig;
    isPersisted: boolean;
  };
}) => {
  const globalAssistantEnabled = useGlobalAIAssistantFeatureEnabled();
  const location = useLocation();

  const initialTab =
    pageConfig.tabs?.find((tab) => tab.isActive) ?? pageConfig.tabs?.[0];
  const primaryFilter = initialTab?.tabContent?.primaryFilter;

  // Intentionally keyed only on pageConfig.id: reset when navigating to a different page, not on every location.state change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const navState = location.state as { preserveScroll?: boolean } | null;
    if (!navState?.preserveScroll) {
      scrollPositionStore.reset();
    }
  }, [pageConfig.id]);

  useEffect(() => {
    if (editMode) return;
    const entity = extractEntityTypeFromPath(location.pathname);
    if (!isAnalyticsEntity(entity)) return;
    const persistable =
      pageConfig.type === PageType.PRIVATE ||
      isAnalyticsRevelioPage(pageConfig.revelioPageId);
    if (!persistable) return;
    setLastViewedTab(entity, location.pathname.slice(1));
  }, [editMode, location.pathname, pageConfig.type, pageConfig.revelioPageId]);

  usePageVisitTracking({
    // Renaming a built-in page changes `page` from then on: add its page id to
    // Mixpanel's "Page name" property and the usage export's RENAMED_PAGE_NAMES
    // (dash-meta) so its visits keep one name.
    page: pageConfig.name,
    pageId: pageConfig.id,
    pageType: pageConfig.type,
  });

  const renderDash = ({ tabContentId }: { tabContentId: string }) => (
    <FullPageError showErrorRatio={DEFAULT_FULL_PAGE_ERROR_THRESHOLD}>
      <CustomDashPreferenceProivder>
        {globalAssistantEnabled ? <GlobalAIAssistantBridge /> : null}
        {/* Reads the page's effective filters; renders nothing outside Data Inspector mode. */}
        <DataInspectorFilterSync />
        <CustomDash tabContentId={tabContentId} />
      </CustomDashPreferenceProivder>
    </FullPageError>
  );

  return (
    <DashboardErrorBoundary>
      <EditModeProvider editMode={editMode}>
        <FilterProvider
          key={getFilterScopeKey(pageConfig)}
          revelioPageId={pageConfig?.revelioPageId}
          primaryFilterId={primaryFilter?.id}
          {...getFilterPersistenceProps(pageConfig)}
          {...(isReport && {
            defaultSelectionOverrides: {
              [FilterId.COMPANY]: [
                { id: '8_256_1116644', name: 'Workday', levelId: 'company' },
              ],
            },
          })}
        >
          <PageStateProvider
            key={pageConfig.id}
            initialPageState={{
              metricOverrides: {},
              selections: {},
              primaryEntities: {},
              multiPrimaryEntities: {},
              chartSwitcherPlotId: null,
              initialSelections: {},
              activeTabId: initialTab?.id ?? '',
            }}
          >
            {reportDocument ? (
              <ReportDocumentProvider
                key={pageConfig.id}
                initialConfig={reportDocument.initialConfig}
                isPersisted={reportDocument.isPersisted}
                isUnsavedDraft={isUnsavedDraft}
              >
                {renderDash}
              </ReportDocumentProvider>
            ) : (
              <PageProvider
                key={`${pageConfig.id}-${editMode ? 'edit' : 'view'}`}
                initialConfig={pageConfig}
                isUnsavedDraft={isUnsavedDraft}
              >
                {({ tabContent }) =>
                  renderDash({ tabContentId: tabContent.id })
                }
              </PageProvider>
            )}
          </PageStateProvider>
          <SyncInitialPreferencesToFilters
            revelioPageId={pageConfig?.revelioPageId}
          />
        </FilterProvider>
      </EditModeProvider>
    </DashboardErrorBoundary>
  );
};

/**
 * CustomDashRoute accepts an optional idOverride that will look up a specific template
 * that's passed in.
 * If no idOverride is provided, it will navigate to the custom-dash creator page
 * If idOverride does not return a template, it will navigate to the home page
 */

/**
 * Whether a flag-gated entity's pages may render. `useUnleashFlag` reads false
 * until Unleash has answered for the logged-in user (the first fetch runs
 * anonymously), so the flag-off redirect waits for that signal: firing on the
 * early false bounced every refresh and deep link off a gated page. The
 * first-load cover stays up meanwhile, since nothing else renders.
 */
export const useFlaggedEntityAccess = (
  flag: FeatureFlag
): 'allowed' | 'pending' | 'denied' => {
  const isEnabled = useUnleashFlag(flag);
  const flagsReadyForUser = useUnleashUserFlagsReady();
  const isPending = !isEnabled && !flagsReadyForUser;
  useFirstLoadGateBlocker(isPending);
  if (isEnabled) return 'allowed';
  return isPending ? 'pending' : 'denied';
};

export const CustomDashRoute = ({ idOverride }: { idOverride?: string }) => {
  const location = useLocation();
  const { templateId, action } = useParams<{
    templateId: string;
    action: RouteAction;
  }>();

  const entityType = extractEntityTypeFromPath(location.pathname);

  if (idOverride) {
    return <RevelioDashboardPage pageId={idOverride} />;
  }

  if (!isValidEntityType(entityType)) {
    return <Navigate to="/" replace />;
  }

  if (templateId) {
    if (action === 'clone') {
      return (
        <NewCustomPage
          entityType={entityType}
          templateId={templateId}
          action="clone"
        />
      );
    }

    return (
      <UserDashboardPage pageId={templateId} editMode={action === 'edit'} />
    );
  }

  return <NewCustomPage entityType={entityType} />;
};

/** Revelio Dashboard Page
 * Doesn't do any page fetching, but just loads the page from local template
 */
const RevelioDashboardPage = ({ pageId }: { pageId: string }) => {
  const pageConfig = getLocalTemplate(pageId);

  if (!pageConfig) return <PageNotFound />;

  // Deliberately unkeyed: keying this on pageConfig.id remounts the whole
  // wrapper — including FilterProvider — on every analytics template switch,
  // which drops the primary-filter selection (breaks cross-template
  // consistency). The inner PageStateProvider/PageProvider already remount per
  // page/tab, so page-scoped state still resets without it.
  return <CustomDashContextWrapper pageConfig={pageConfig} editMode={false} />;
};

/** User Dashboard Page
 * Fetches the page by id
 */
const UserDashboardPage = ({
  pageId,
  editMode,
}: {
  pageId: string;
  editMode: boolean;
}) => {
  const location = useLocation();
  const { loggedInUser } = useGetLoggedInUser();

  const newReportLayoutEnabled = useUnleashFlag(
    FeatureFlag.CustomReportNewLayout
  );
  const isReportPath =
    extractEntityTypeFromPath(location.pathname) === 'report';
  const documentLookupEnabled = isReportPath && !getLocalReportTemplate(pageId);
  const {
    data: persistedDocument,
    isLoading: isLoadingDocument,
    isError: documentLookupFailed,
  } = useFetchReportDocument(pageId, { enabled: documentLookupEnabled });
  const { data: pageConfig, isLoading } = useFetchPageConfig(pageId, {
    enabled:
      !documentLookupEnabled || (!isLoadingDocument && !persistedDocument),
  });

  const reportDocument = useMemo(() => {
    if (!isReportPath) return undefined;
    if (documentLookupEnabled && persistedDocument) {
      return { initialConfig: persistedDocument, isPersisted: true };
    }
    if (newReportLayoutEnabled && pageConfig?.type === PageType.REPORT) {
      return {
        initialConfig: reportDocumentFromPage(pageConfig),
        isPersisted: false,
      };
    }
    return undefined;
  }, [
    isReportPath,
    documentLookupEnabled,
    persistedDocument,
    newReportLayoutEnabled,
    pageConfig,
  ]);

  const effectivePageConfig = useMemo(
    () =>
      reportDocument
        ? pageFromReportDocument(reportDocument.initialConfig)
        : pageConfig,
    [reportDocument, pageConfig]
  );

  useEffect(() => {
    mixpanel.track(BuildADashTrackingEvents.VIEW_CUSTOM_PAGE, {
      page_id: pageId,
    });
  }, [pageId]);

  if (
    (isLoading && !persistedDocument) ||
    (documentLookupEnabled && isLoadingDocument)
  ) {
    return <CenterLoading />;
  }

  if (!effectivePageConfig) return <PageNotFound />;

  if (editMode && !loggedInUser?.id) {
    return <CenterLoading />;
  }

  // A non-404 lookup failure may hide an existing document, so editing the page copy is blocked.
  if (editMode && documentLookupFailed) {
    return (
      <Navigate
        to={buildPathFromPage(effectivePageConfig, { search: location.search })}
        replace
      />
    );
  }

  // Only the owner is allowed to enter edit mode
  if (editMode && loggedInUser.id !== effectivePageConfig.owner.id) {
    return (
      <Navigate
        to={buildPathFromPage(effectivePageConfig, { search: location.search })}
        replace
      />
    );
  }

  return (
    <CustomDashContextWrapper
      pageConfig={effectivePageConfig}
      editMode={editMode}
      reportDocument={reportDocument}
    />
  );
};
type PageLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: Page }
  | { status: 'error'; error: Error };

function NewCustomPage({
  entityType,
  templateId,
  action,
}: {
  entityType: EntityType;
  templateId?: string;
  action?: 'clone';
}) {
  const { loggedInUser } = useGetLoggedInUser();
  const { pages, loading: pagesLoading } = usePageManager();
  const brand = useBrand();
  const location = useLocation();

  const newReportLayoutEnabled = useUnleashFlag(
    FeatureFlag.CustomReportNewLayout
  );
  const documentLookupEnabled = entityType === 'report';
  const {
    data: reportDocuments,
    isLoading: documentsLoading,
    isError: documentsLookupFailed,
  } = useReportDocumentsQuery({ enabled: documentLookupEnabled });

  const localTemplates = getLocalTemplates();
  const generationKey = `${entityType}:${templateId ?? ''}:${action ?? ''}`;
  const generatedKeyRef = useRef<string>('');

  const [state, setState] = useState<PageLoadState>(() =>
    pagesLoading ? { status: 'loading' } : { status: 'idle' }
  );

  const owner = useMemo(
    () => ({
      id: loggedInUser.id ?? '',
      username: loggedInUser.username ?? '',
    }),
    [loggedInUser.id, loggedInUser.username]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (pagesLoading || (documentLookupEnabled && documentsLoading)) {
      setState({ status: 'loading' });
      return;
    }

    // Terminal error: don't retry on incidental dep changes (e.g. `pages`
    // array reference updates). Base's generationKey guard below handles
    // the success terminal case.
    if (state.status === 'error') {
      return;
    }

    // Keep already-generated config unless the route inputs changed.
    if (
      state.status === 'success' &&
      generatedKeyRef.current === generationKey
    ) {
      return;
    }

    const sourceMayBeReportDocument =
      templateId !== undefined &&
      !localTemplates.some((template) => template.id === templateId);
    if (
      documentLookupEnabled &&
      documentsLookupFailed &&
      sourceMayBeReportDocument
    ) {
      setState({
        status: 'error',
        error: new Error('Failed to load report documents'),
      });
      return;
    }

    setState({ status: 'loading' });
    let cancelled = false;

    (async () => {
      try {
        const existingPages = [
          ...(documentLookupEnabled
            ? withReportDocumentPages(pages, reportDocuments ?? [])
            : pages),
          ...localTemplates,
        ];
        const config = await generatePageDraftConfig({
          owner,
          existingPages,
          entityType,
          templateId,
          action,
          clientName: brand.clientName,
        });
        if (cancelled) {
          return;
        }
        // RESOLVE-FIRST page-gen (Phase C): when the assistant pre-generated a
        // layout for THIS draft path (Build-chip click), seed the fresh config
        // with it so the page MOUNTS complete — widgets simply data-load; no
        // empty draft, no on-page build stream. Consume-once + TTL'd: a stale
        // or absent stash falls through to the normal empty draft.
        const pending = takeGeneratedPage(location.pathname);
        const firstTab = config.tabs?.[0];
        if (pending && firstTab?.tabContent) {
          const primaryFilterId =
            PRIMARY_FILTER_BY_AI_ENTITY[
              entityType as keyof typeof PRIMARY_FILTER_BY_AI_ENTITY
            ];
          if (primaryFilterId) {
            firstTab.tabContent = seedTabContentWithLlmContent(
              firstTab.tabContent,
              pending.content,
              primaryFilterId
            );
            // Report the MINTED widget items back to the stash producer (the
            // drawer): seeding mints fresh item uuids, so this is the only
            // place the ids the widgets mount with exist — the drawer arms
            // its landing-narration collector with them (grounded narration
            // for resolve-first builds; see seeded-build-narration.ts).
            pending.onSeeded?.(
              (firstTab.tabContent as { items?: PositionedItem[] }).items ?? []
            );
          }
        }
        generatedKeyRef.current = generationKey;
        setState({ status: 'success', data: config });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState({
          status: 'error',
          error:
            error instanceof Error
              ? error
              : new Error('Failed to generate page configuration'),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    pagesLoading,
    pages,
    owner,
    entityType,
    templateId,
    action,
    brand.clientName,
    generationKey,
    state.status,
    documentLookupEnabled,
    documentsLoading,
    documentsLookupFailed,
    reportDocuments,
  ]);

  const sourceIsReportDocument =
    templateId !== undefined &&
    (reportDocuments ?? []).some((document) => document.id === templateId);

  const draftReportDocument = useMemo(
    () =>
      state.status === 'success' &&
      (newReportLayoutEnabled || sourceIsReportDocument) &&
      state.data.type === PageType.REPORT
        ? {
            initialConfig: reportDocumentFromPage(state.data),
            isPersisted: false,
          }
        : undefined,
    [state, newReportLayoutEnabled, sourceIsReportDocument]
  );

  if (!loggedInUser?.id) {
    return <Navigate to="/" replace />;
  }

  switch (state.status) {
    case 'idle':
    case 'loading':
      return <CenterLoading />;

    case 'error':
      return <PageNotFound />;

    case 'success':
      return (
        <CustomDashContextWrapper
          pageConfig={state.data}
          editMode
          isReport={entityType === 'report'}
          isUnsavedDraft
          reportDocument={draftReportDocument}
        />
      );
  }
}
